import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Tooltip, TextField, MenuItem,
    Switch, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { solidBtn, ghostBtn, field, card, Panel, EmptyState, ConfirmDialog, LoadAlert } from '../uiKit';
import { listFields, createField, updateField, deleteField, FIELD_TYPES, HAS_OPTIONS } from '../../Api/interviewFields';
import { apiErrorMessage } from '../../Api/http';

const EMPTY = { id: null, label: '', fieldType: 'Text', isRequired: false, options: [] };
const typeLabel = (t) => FIELD_TYPES.find((x) => x.key === t)?.label || t;

export default function InterviewFieldsTab() {
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [draft, setDraft] = useState(null);
    const [optionText, setOptionText] = useState('');
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setFields(await listFields());
            setLoadError('');
        } catch (err) {
            setFields([]);
            setLoadError(apiErrorMessage(err, 'Could not load interview fields.'));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openNew = () => { setDraft({ ...EMPTY }); setOptionText(''); };
    const openEdit = (f) => { setDraft({ ...f, options: [...(f.options || [])] }); setOptionText(''); };

    const needsOptions = HAS_OPTIONS.has(draft?.fieldType);
    const canSave = Boolean(draft?.label.trim()) && (!needsOptions || (draft?.options || []).length > 0);

    const submit = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        try {
            if (draft.id) await updateField(draft); else await createField(draft);
            await load();
            notify(draft.id ? `"${draft.label.trim()}" updated.` : `"${draft.label.trim()}" added to the interview form.`);
            setDraft(null);
        } catch (err) {
            // The API explains exactly what is wrong (missing label, empty
            // options, changed fieldType) — pass that through rather than a
            // generic failure.
            notify(apiErrorMessage(err, 'Could not save the field.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (f) => {
        setConfirm(null);
        try {
            await deleteField(f.id);
            await load();
            notify(`"${f.label}" removed from new interview forms.`);
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the field.'), 'error');
        }
    };

    const addOption = () => {
        const v = optionText.trim();
        if (!v) return;
        setDraft((d) => (d && d.options.includes(v) ? d : { ...d, options: [...d.options, v] }));
        setOptionText('');
    };

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel
                title="Interview Form Fields"
                icon={TuneRoundedIcon}
                chip={`${fields.length} custom field${fields.length === 1 ? '' : 's'}`}
                hint="These appear on Schedule an Interview, under the standard fields"
                action={(
                    <Button startIcon={<AddRoundedIcon />} onClick={openNew} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>
                        Add Field
                    </Button>
                )}
            >
                {loading ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={26} /></Box>
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : fields.length === 0 ? (
                    <EmptyState
                        icon={TuneRoundedIcon}
                        title="No custom fields yet"
                        hint="Add one and it shows up on the Schedule an Interview form straight away — no code change needed."
                    />
                ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {fields.map((f) => (
                            <Stack
                                key={f.id}
                                direction="row"
                                spacing={1.4}
                                sx={{
                                    alignItems: 'center', px: 1.6, py: 1.4, borderRadius: '9px',
                                    border: '1px solid #EEF0F6', bgcolor: '#fff',
                                    '&:hover': { borderColor: PRIMARY_BORDER },
                                }}
                            >
                                <DragIndicatorRoundedIcon sx={{ fontSize: 18, color: '#D5DAE3', flexShrink: 0 }} />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }} noWrap>{f.label}</Typography>
                                        {f.isRequired && (
                                            <Chip label="Required" size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: '#FEF3F5', color: '#BE123C' }} />
                                        )}
                                    </Stack>
                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>
                                        {typeLabel(f.fieldType)}
                                        {HAS_OPTIONS.has(f.fieldType) && f.options?.length ? ` · ${f.options.join(', ')}` : ''}
                                    </Typography>
                                </Box>

                                <Tooltip arrow title="Edit field">
                                    <IconButton size="small" onClick={() => openEdit(f)} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                        <EditRoundedIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip arrow title="Remove field">
                                    <IconButton size="small" onClick={() => setConfirm(f)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ))}
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(draft)} onClose={() => !saving && setDraft(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
                        {draft?.id ? 'Edit field' : 'New interview field'}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        It appears on Schedule an Interview for everyone in this entity.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '14px !important' }}>
                    <Stack spacing={1.8}>
                        <TextField
                            label="Field label" size="small" fullWidth autoFocus
                            placeholder="e.g. Interview location floor"
                            value={draft?.label || ''}
                            onChange={(e) => setDraft((d) => d && { ...d, label: e.target.value })}
                            helperText={draft?.fieldKey ? `Key: ${draft.fieldKey}` : ' '}
                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                        />

                        <TextField
                            select label="Field type" size="small" fullWidth
                            value={draft?.fieldType || 'Text'}
                            disabled={Boolean(draft?.id)}
                            helperText={draft?.id ? 'Type cannot change after a field is created.' : ' '}
                            onChange={(e) => setDraft((d) => d && { ...d, fieldType: e.target.value })}
                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                        >
                            {FIELD_TYPES.map((t) => <MenuItem key={t.key} value={t.key} sx={{ fontSize: 13.5 }}>{t.label}</MenuItem>)}
                        </TextField>

                        {needsOptions && (
                            <Box sx={{ ...card, p: 1.5, boxShadow: 'none', bgcolor: '#FAFAFC' }}>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>
                                    {draft?.fieldType === 'MultiSelect' ? 'Multi-select options' : 'Dropdown options'}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small" fullWidth placeholder="Type an option and press Enter"
                                        value={optionText}
                                        onChange={(e) => setOptionText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                                        sx={field}
                                    />
                                    <Button onClick={addOption} disabled={!optionText.trim()} sx={{ ...ghostBtn, height: 40, px: 1.6, flexShrink: 0 }}>Add</Button>
                                </Stack>
                                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.7, mt: 1.2 }}>
                                    {(draft?.options || []).length === 0
                                        ? <Typography sx={{ fontSize: 12, color: '#B6BDC9', fontStyle: 'italic' }}>At least one option is needed.</Typography>
                                        : draft.options.map((o) => (
                                            <Chip
                                                key={o} label={o} size="small"
                                                onDelete={() => setDraft((d) => d && { ...d, options: d.options.filter((x) => x !== o) })}
                                                sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }}
                                            />
                                        ))}
                                </Stack>
                            </Box>
                        )}

                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 0.4 }}>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Required</Typography>
                                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Block scheduling until it is filled in</Typography>
                            </Box>
                            <Switch
                                checked={Boolean(draft?.isRequired)}
                                onChange={(e) => setDraft((d) => d && { ...d, isRequired: e.target.checked })}
                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY } }}
                            />
                        </Stack>
                    </Stack>

                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 1.6 }}>
                        Shared with everyone in this company.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDraft(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button
                        onClick={submit} disabled={!canSave || saving}
                        startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{ ...solidBtn, height: 40, px: 2.4, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}
                    >
                        {draft?.id ? 'Save field' : 'Add field'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => setConfirm(null)}
                onConfirm={() => remove(confirm)}
                title="Remove this field?"
                body={`"${confirm?.label}" is removed from new interview forms. Already-scheduled interviews keep their answers and still show it.`}
                confirmLabel="Remove"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3200} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
